import React, {
  useState,
  useEffect,
  useContext,
  useMemo,
  useRef,
} from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceLine,
  ReferenceArea,
  Area,
  Brush,
  ResponsiveContainer,
} from 'recharts';
import { ThemeContext } from 'styled-components';
import { format } from 'date-fns';
import { useSelector } from 'react-redux';
import * as s from './styled-line-chart';
import {
  GetColor,
  calcChartStats,
  downsampleLTTB,
  historicKey,
} from '../../utils';
import { dateFormats } from '../../constants';
import { buildSimulatedSeries } from '../../utils/simulator';
import { useSimulator } from '../../contexts/simulator';
import {
  getThingTrips,
  getThingBlocks,
  fetchHistoricDay,
  fetchHistoricWeek,
  fetchHistoricMonth,
} from '../../services';
import EventTimeline from '../event-timeline/event-timeline';

const periods = [
  { label: 'mês', value: 'monthly' },
  { label: 'semana', value: 'weekly' },
  { label: 'dia', value: 'daily' },
];

// Mapeamento periodo -> dias de janela. Usado pra calcular o offset do
// 'ghost line' (curva tracejada do periodo anterior sobreposta).
const PERIOD_INTERVAL_DAYS = {
  daily: 1,
  weekly: 7,
  monthly: 30,
};
const DAY_MS = 24 * 60 * 60 * 1000;

// Resolve qual funcao de fetch usar por periodo.
const fetchFnFor = {
  daily: fetchHistoricDay,
  weekly: fetchHistoricWeek,
  monthly: fetchHistoricMonth,
};

const parseTimestampMs = (value) => {
  if (value === undefined || value === null) return null;
  const numeric = Number(value);
  if (Number.isFinite(numeric)) {
    return Math.abs(numeric) < 1e12 ? numeric * 1000 : numeric;
  }
  const parsed = new Date(value).getTime();
  return Number.isFinite(parsed) ? parsed : null;
};

// state.historic agora e keyed por historicKey(thingId, variable) — isola
// o cache por maquina (antes colidia: 'Vazao' de maquinas diferentes
// compartilhavam entrada, e trocar de maquina nao atualizava o grafico).
// TODO(analytics): #9 Multi-serie (overlay de N maquinas na mesma curva)
// ainda pendente — exige montar compareSeries de varias things.

// Dot customizado mantido para os marcadores de 2h/24h existentes. Cor
// agora vem do theme em vez de hardcoded.
// Factory do dot custom da Line principal. Retorna SVG render quando:
//   1. payload.name === '2h' / '24h' → marker historico tradicional
//   2. timestamp esta em outlierTimestamps (Set) → ponto OUTLIER, marca
//      em VERMELHO pra operador detectar instantaneamente. So acontece
//      em modo avancado (onde outlier detection rodou).
// Demais pontos retornam null (Recharts nao renderiza dot).
const makeCustomizedDot = (dotColor, outlierColor, outlierTimestamps) =>
  function CustomizedDot({ payload, cx, cy }) {
    // Outlier check primeiro — quando data tem milhares de pontos, marcar
    // outliers é a info mais critica visualmente.
    if (
      outlierTimestamps &&
      payload &&
      outlierTimestamps.has(payload.timestamp)
    ) {
      return (
        <svg x={cx - 5} y={cy - 5} width={10} height={10}>
          <circle cx="5" cy="5" r="4" fill={outlierColor} fillOpacity={0.85} />
          <circle cx="5" cy="5" r="4" fill="none" stroke={outlierColor} strokeOpacity={0.4} strokeWidth={1.5} />
        </svg>
      );
    }
    if (payload.name === '2h' || payload.name === '24h') {
      return (
        <svg x={cx - 4} y={cy - 4} width={11} height={11} fill={dotColor}>
          <g transform="translate(4 4)">
            <circle r="4" fill={dotColor} />
            <circle r="2" fill={dotColor} />
          </g>
        </svg>
      );
    }
    return null;
  };

// Calcula min/max do Y-axis com 5% de padding em cima e em baixo, garantindo
// que a linha nao encoste nas bordas do grafico. Se min===max (todos valores
// iguais), abre uma janela de +/-1 para o grafico nao colapsar.
const padDomain = (min, max) => {
  if (!Number.isFinite(min) || !Number.isFinite(max)) return [0, 0];
  if (min === max) return [min - 1, max + 1];
  const padding = (max - min) * 0.05;
  return [min - padding, max + padding];
};

// Escala uniforme COM numeros redondos: combina distribuicao uniforme
// (N linhas) com snap pra step "nice" (multiplos de 1/2/5 * 10^k). Antes
// gerava 218.3, 222.5, 226.7... — agora 220, 230, 240. Domain estende
// pra acomodar todos os ticks (Recharts filtra ticks fora do domain).
// targetLines e aproximado: pode sair 8-12 ticks dependendo do step
// nice encontrado, mas valores ficam sempre legiveis.
const evenlyDistributedTicks = (min, max, targetLines) => {
  if (!Number.isFinite(min) || !Number.isFinite(max) || min === max) {
    return null;
  }
  // 10% de tolerancia em cima e embaixo (era 5% antes). Folga maior
  // pra: 1) linha nao encostar nas bordas do chart; 2) dips/spikes
  // moderados continuarem visiveis dentro da area; 3) ticks "nice"
  // (multiplos de 1/2/5) se acomodarem melhor sem precisar expandir
  // demais via floor/ceil.
  const padding = (max - min) * 0.1;
  const lo = min - padding;
  const hi = max + padding;
  const range = hi - lo;
  const roughStep = range / targetLines;
  // Snap pra potencia de 10 * fator 1/2/5 (Wilkinson "Nice Numbers"
  // simplificado — mesma logica que usavamos antes)
  const exp = Math.floor(Math.log10(roughStep));
  const fraction = roughStep / 10 ** exp;
  let niceFraction;
  if (fraction < 1.5) niceFraction = 1;
  else if (fraction < 3) niceFraction = 2;
  else if (fraction < 7) niceFraction = 5;
  else niceFraction = 10;
  const step = niceFraction * 10 ** exp;
  const niceMin = Math.floor(lo / step) * step;
  const niceMax = Math.ceil(hi / step) * step;
  const ticks = [];
  for (let v = niceMin; v <= niceMax + step * 0.5; v += step) {
    ticks.push(Number(v.toFixed(10)));
  }
  return { ticks, domain: [niceMin, niceMax] };
};

// Steps "nice" em ms — sequencia crescente, usados pra escolher
// granularidade do X conforme largura disponivel. Cobertura: 15min ate
// 1 semana. Algoritmo pega o MENOR step que ainda cabe maxTicks
// labels (anti-overlap). Resultado: chart largo mostra 15min/30min/1h,
// chart estreito mostra 6h/12h/1dia — labels nunca cortam.
const NICE_TIME_STEPS_MS = [
  15 * 60 * 1000,
  30 * 60 * 1000,
  60 * 60 * 1000,
  2 * 60 * 60 * 1000,
  3 * 60 * 60 * 1000,
  4 * 60 * 60 * 1000,
  6 * 60 * 60 * 1000,
  8 * 60 * 60 * 1000,
  12 * 60 * 60 * 1000,
  24 * 60 * 60 * 1000,
  2 * 24 * 60 * 60 * 1000,
  7 * 24 * 60 * 60 * 1000,
];

// Largura aproximada de um label do X em px (ex.: "17:00" com fontSize
// 11 + gap). Usado pra calcular maxTicks que cabem na largura do plot.
const X_LABEL_WIDTH_PX = 55;

const formatChartTimestamp = (timestamp, pattern = 'd/L/y - HH:mm') => {
  const numericTimestamp = Number(timestamp);
  if (!Number.isFinite(numericTimestamp)) return '';

  const timestampMs =
    Math.abs(numericTimestamp) < 1e11
      ? numericTimestamp * 1000
      : numericTimestamp;
  const date = new Date(timestampMs);

  if (Number.isNaN(date.getTime())) return '';

  return format(date, pattern);
};

// Ticks do X axis em fronteiras REDONDAS de tempo, com densidade
// adaptativa pela largura do plot. Antes o step era fixo por periodo
// (daily=1h, weekly=12h, monthly=1dia) — mas em chart estreito 1h
// gerava 24 labels que se sobrepunham e cortavam. Agora pegamos o
// menor step "nice" que ainda cabe na largura disponivel.
// minStepMs: piso do passo. Steps "nice" menores que ele sao ignorados —
// assim o eixo nunca fica mais fino que esse minimo (ex.: 1h) mesmo em
// telas muito largas; quando 1h nao cabe, sobe pro proximo (2h, 3h...).
const roundedTimeIndices = (data, plotWidthPx, minStepMs = 0) => {
  if (!data || data.length < 2) return [];
  const firstTs = data[0].timestamp * 1000;
  const lastTs = data[data.length - 1].timestamp * 1000;
  const totalSpanMs = lastTs - firstTs;

  // Quantos labels cabem horizontalmente. Min 4 pra nao virar 1-2
  // ticks soltos em containers extremamente estreitos.
  const maxTicks = Math.max(4, Math.floor(plotWidthPx / X_LABEL_WIDTH_PX));

  // Steps candidatos respeitando o piso minStepMs.
  const candidates = NICE_TIME_STEPS_MS.filter((s) => s >= minStepMs);
  const pool = candidates.length ? candidates : NICE_TIME_STEPS_MS;

  // Escolhe o MENOR step "nice" que produz <= maxTicks ticks.
  // Fallback: maior step disponivel se nada couber (shouldn't happen
  // dado limite de 1 semana, mas defensivo).
  let stepMs = pool[pool.length - 1];
  for (let i = 0; i < pool.length; i += 1) {
    const candidate = pool[i];
    if (totalSpanMs / candidate <= maxTicks) {
      stepMs = candidate;
      break;
    }
  }

  // Comeca na proxima fronteira redonda apos firstTs. Snap depende do
  // step: stepMs >= 1 dia → meia-noite; >= 1h → hora cheia; senao →
  // proxima fronteira multipla do step (ex.: step=15min snap pra :00
  // /:15/:30/:45).
  const startDate = new Date(firstTs);
  if (stepMs >= 24 * 60 * 60 * 1000) {
    startDate.setHours(0, 0, 0, 0);
  } else if (stepMs >= 60 * 60 * 1000) {
    startDate.setMinutes(0, 0, 0);
  } else {
    // sub-hora: snap pro multiplo do step dentro da hora atual
    const minutesPerStep = stepMs / 60000;
    const snapped = Math.floor(startDate.getMinutes() / minutesPerStep) * minutesPerStep;
    startDate.setMinutes(snapped, 0, 0);
  }
  let cursor = startDate.getTime();
  if (cursor < firstTs) cursor += stepMs;

  const indicesSet = new Set();
  // Busca otimizada: como data e ordenado por timestamp, usamos um
  // ponteiro avancando linearmente em vez de scan completo por tick.
  let searchFrom = 0;
  while (cursor <= lastTs) {
    let bestIdx = searchFrom;
    let bestDiff = Infinity;
    for (let i = searchFrom; i < data.length; i += 1) {
      const diff = Math.abs(data[i].timestamp * 1000 - cursor);
      if (diff < bestDiff) {
        bestDiff = diff;
        bestIdx = i;
      } else if (data[i].timestamp * 1000 > cursor) {
        // ja passamos do alvo — diff so vai crescer
        break;
      }
    }
    indicesSet.add(bestIdx);
    searchFrom = bestIdx; // proximo tick comeca daqui pra frente
    cursor += stepMs;
  }
  return Array.from(indicesSet).sort((a, b) => a - b);
};

// Densidade do grid horizontal. ~10 linhas (11 ticks) com numeros
// redondos via evenlyDistributedTicks. O X vem do roundedTimeIndices
// que escolhe sua propria densidade conforme o periodo.
const Y_AXIS_LINES = 10;

// Converte chartY pixel (relativo ao SVG do Recharts) pro valor no
// dominio Y atual. Usado pelo drag-to-zoom 2D pra capturar a Y do
// cursor em data values (ReferenceArea so aceita data values, nao
// pixels). Aproximacao: assume margem top=12px e XAxis ~30px embaixo
// — constantes batem com nosso <LineChart margin={...}> + width do
// XAxis. Pequenas variacoes (1-3px) nao quebram UX (operador vai ver
// o retangulo "perto" do cursor, indistinguivel a olho).
const CHART_TOP_MARGIN = 12;
const CHART_BOTTOM_PAD = 34; // bottom margin + XAxis label height
const pixelYToValue = (chartY, yDomain, chartHeightPx) => {
  if (!Number.isFinite(chartY) || !yDomain) return null;
  const plotHeight = chartHeightPx - CHART_TOP_MARGIN - CHART_BOTTOM_PAD;
  if (plotHeight <= 0) return null;
  const relativeY = Math.max(
    0,
    Math.min(plotHeight, chartY - CHART_TOP_MARGIN)
  );
  // SVG Y cresce pra BAIXO; data Y cresce pra CIMA → inverter.
  const ratio = 1 - relativeY / plotHeight;
  return yDomain[0] + ratio * (yDomain[1] - yDomain[0]);
};

// Label custom pra ReferenceLine de anotacoes — renderiza texto +
// botao × clicavel inline. Usado pelo prop label do ReferenceLine
// (Recharts aceita funcao que retorna SVG). textAnchor=middle no
// texto + × deslocado pra direita pelo width estimado (chars*5.5px,
// aproximacao razoavel pra fontSize 10 com sans-serif).
const AnnotationLabel = ({ viewBox, text, color, onRemove }) => {
  if (!viewBox) return null;
  const { x, y } = viewBox;
  const display = text.length > 24 ? `${text.slice(0, 24)}…` : text;
  // Width estimate: emoji + space + chars × ~5.5px @ fontSize 10
  const estCharWidth = 5.5;
  const textWidth = (display.length + 3) * estCharWidth;
  const buttonOffsetX = textWidth / 2 + 8;
  return (
    <g>
      <text
        x={x}
        y={y - 5}
        fill={color}
        fontSize={10}
        fontWeight={700}
        textAnchor="middle"
        style={{ pointerEvents: 'none' }}
      >
        {`\u{1F4DD} ${display}`}
      </text>
      {onRemove && (
        <g
          transform={`translate(${x + buttonOffsetX}, ${y - 12})`}
          onClick={(e) => {
            // Stop propagation pra nao disparar onMouseDown do
            // drag-to-zoom do parent <LineChart> (delete e click,
            // nao drag — o drag fica desabilitado durante click).
            e.stopPropagation();
            onRemove();
          }}
          style={{ cursor: 'pointer' }}
        >
          {/* Hit area maior que o icone — facilita click em
              monitores TV CCO + touch fica viavel. SVG events
              respeitam o fill da forma; fill transparente nao
              recebe eventos, fill solido recebe. */}
          <circle
            cx={6}
            cy={6}
            r={8}
            fill="rgba(0, 0, 0, 0.35)"
            stroke={color}
            strokeWidth={1}
            strokeOpacity={0.6}
          />
          <text
            x={6}
            y={10}
            fill="#fff"
            fontSize={12}
            fontWeight={800}
            textAnchor="middle"
            style={{ pointerEvents: 'none' }}
          >
            ×
          </text>
        </g>
      )}
    </g>
  );
};

// Threshold pra ativar downsampling LTTB. Acima disso, Recharts/SVG
// rendering fica laggy. 2000 pontos cabem confortavelmente — mantemos
// fidelidade visual + perf decente.
const DOWNSAMPLE_THRESHOLD = 2000;
const DOWNSAMPLE_TARGET = 1000;

const LineChartComponent = ({
  selectedVar,
  setSelectedVar,
  selectedPeriod,
  setSelectedPeriod,
  chartType = 'float',
  thingId,
  thing,
  loadingGraph,
  setLoadingGraph,
  // 'basic' (default): visual limpo pra glance — sem switcher, sem
  //   brush, sem ghost, sem event timeline, smoothing aplicado, X com
  //   menos ticks. Foco em "ta rodando normal?".
  // 'advanced': visual completo — todos os controles habilitados, raw
  //   data, brush + ghost + switcher + event timeline. Foco em
  //   investigacao / analise pos-evento.
  mode = 'basic',
  // Callback acionado quando o usuario clica em "Modo avancado" no
  // header. A tela que monta o chart decide o que abrir (modal,
  // expand, etc.). Quando nao fornecido, o botao nao aparece.
  onRequestAdvanced,
  // Series secundarias pra overlay multi-variavel (so faz sentido em
  // mode='advanced'). Cada item: { ihmname, name, unit, color, data:
  // [{ timestamp, value }] }. Cada serie ganha seu proprio YAxis a
  // direita (yAxisId=ihmname) pra escalas independentes — vazao em
  // L/s nao deve compartilhar Y com pressao em mca.
  compareSeries = [],
  // Stats precomputados (calcChartStats) injetados pelo parent quando
  // ele ja calculou — evita rodar duas vezes (modal calcula 1x e passa
  // pra LineChart + ChartStatsPanel). Quando undefined, o LineChart
  // calcula internamente (caso isAdvanced sem precomputed).
  precomputedStats,
  // Notifica o parent quando o usuario zooma via drag-to-select ou
  // Brush. Recebe { startTs, endTs, yMin?, yMax? } ou null quando o
  // zoom e desfeito. yMin/yMax aparecem quando foi box-zoom (selecao
  // 2D); sem eles e so X-zoom (drag horizontal).
  onZoomChange,
  // Domain Y forcado pelo parent (box-zoom). Quando setado, sobrescreve
  // o yAxisConfig.domain — o operador ve so a faixa Y do quadrante
  // selecionado.
  zoomYDomain,
  // Trigger pra resetar o zoom externamente. Quando o valor muda
  // (incrementa em qualquer numero), LineChart limpa brushRange.
  // Padrao "key change" pra forcar reset sem precisar de
  // useImperativeHandle.
  resetZoomKey,
  // Anotacoes do operador (persistencia localStorage no parent).
  // [{ id, timestamp, text, createdAt }]. Renderizadas como
  // ReferenceLine vertical com label do texto truncado + botao × pra
  // remover inline. Quando onRemoveAnnotation passado, o × aparece;
  // sem callback, label so leitura.
  annotations,
  onRemoveAnnotation,
  // Modo 'colocar anotacao' — quando true, o chart vira cursor
  // crosshair e desabilita drag-to-zoom; click no chart dispara
  // onAnnotationPlace(timestamp). Modal controla o estado.
  annotationMode,
  onAnnotationPlace,
  // Segmentos operacionais (running/stopped) vindos do parent — quando
  // setado (e isAdvanced), renderiza bandas de fundo translucidas
  // pra cada periodo de PARADA. Periodos rodando ficam transparentes
  // (assume-se default). null/undefined desativa.
  operationalSegments,
  // Timestamp (epoch ms) de um evento selecionado no Log da maquina —
  // desenha um marcador vertical destacado naquele instante pra
  // correlacionar o evento com a curva. null/undefined nao desenha.
  // Quando o evento cai fora da janela exibida, o marcador nao aparece
  // (mesmo criterio dos eventReferenceLines).
  focusTs,
}) => {
  const isAdvanced = mode === 'advanced';
  const [data, setData] = useState([]);
  // Suavizacao (media movel) no modo avancado. Default ON pra leitura
  // agradavel — igual ao basico, que sempre suaviza. Operador desliga
  // pra ver o dado BRUTO (analise fina de ruido/eventos).
  const [smoothAdvanced, setSmoothAdvanced] = useState(true);
  const { colors } = useContext(ThemeContext);
  // Antes selecionava state.historic inteiro — re-renderizava sempre que
  // QUALQUER variavel no histórico atualizava. Agora seleciona apenas a
  // variavel exibida no grafico (selectedVar.ihmname).
  const variableHistoric = useSelector((state) =>
    selectedVar && selectedVar.ihmname && state.historic
      ? state.historic[historicKey(thingId, selectedVar.ihmname)]
      : null
  );
  // Seleciona apenas o snapshot ao vivo da thing atual. Ele alimenta o
  // indicador de frescor e tambem complementa a borda direita do grafico
  // enquanto o historico consolidado ainda nao chegou na Athena.
  const realtimeThingData = useSelector((state) =>
    state.aplicacao.velocimetro &&
    state.aplicacao.velocimetro[thingId]
      ? state.aplicacao.velocimetro[thingId]
      : null
  );
  const lastUpdateTs = realtimeThingData && realtimeThingData.ts;
  // Eventos (trips + blocks) da maquina selecionada — renderizados como
  // ReferenceLine vertical no chart pra correlacionar evento com queda da
  // curva. EventTimeline tambem usa a mesma info via fetch propria; aqui
  // mantemos cache local pra nao re-fetchar.
  const [events, setEvents] = useState([]);
  // 'Ghost line' — dados do periodo IMEDIATAMENTE ANTERIOR da mesma
  // variavel/thing. Plotado como curva tracejada cinza sobreposta a curva
  // atual pra comparar "hoje vs ontem" / "semana atual vs semana passada".
  const [ghostData, setGhostData] = useState([]);
  // Toggle pra ativar/desativar a ghost line. Default off — operador liga
  // sob demanda. Estado por sessao do componente.
  const [showGhost, setShowGhost] = useState(false);
  // Quick switcher de variavel: dropdown que abre ao clicar no nome no
  // header. Permite trocar a curva sem voltar ao Velocimetro pra clicar
  // no gauge.
  const [switcherOpen, setSwitcherOpen] = useState(false);

  // Drag-to-zoom 2D: operador clica e arrasta sobre uma regiao da
  // curva pra zoom em X (tempo) E Y (valor). selection guarda os
  // extremos do retangulo enquanto o mouse esta pressionado
  // (renderizado como ReferenceArea pra feedback visual em data
  // values). brushRange guarda o zoom X final (controla Brush). O
  // zoomYDomain (vem do parent) controla o YAxis quando setado. null
  // em qualquer campo = sem zoom naquele eixo.
  const [selection, setSelection] = useState(null);
  // { startIdx, endIdx, startY, endY }
  const [brushRange, setBrushRange] = useState(null); // { startIndex, endIndex }
  // Bridge entre onMouseUp (commit do zoom) e o useEffect que emite
  // onZoomChange: a info Y do drag precisa "pegar carona" no proximo
  // dispatch porque brushRange so guarda X. Set no commit, read no
  // emitter, clear apos uso.
  const pendingZoomYRef = useRef(null);

  // Minimo de pontos pra ativar zoom — evita disparar zoom em click
  // simples (sem arrastar). 3 pontos no X = ~3 minutos de dados.
  const MIN_ZOOM_RANGE = 3;

  // Reset externo: quando resetZoomKey muda (incrementado pelo parent),
  // limpa brushRange. Padrao "key change" — simples, sem refs.
  useEffect(() => {
    if (resetZoomKey !== undefined) {
      setBrushRange(null);
    }
  }, [resetZoomKey]);
  // Lista de variaveis disponiveis pra essa thing (populada pelo
  // Velocimetro via dispatch IOT/SET_VARIABLES_THING). Source of truth
  // compartilhada via Redux.
  const availableVariables = useSelector((state) =>
    thing && thing.thing && state.iot && state.iot[thing.thing]
      ? state.iot[thing.thing].variables || []
      : []
  );
  // Ref pra fechar o switcher ao clicar fora.
  const switcherRef = useRef(null);

  // Largura atual do canvas do chart — alimenta a densidade adaptativa
  // dos ticks do X. Atualizada via ResizeObserver com THROTTLE de 150ms
  // pra responder ao redimensionamento da janela / sidebar / split-view
  // sem cascata de re-renders. Sem throttle, ResizeObserver dispara
  // dezenas de vezes durante o arrasto de uma janela — cada um custava
  // recomputar ticks do X e re-render do Recharts (chart 'tremia').
  const chartCanvasRef = useRef(null);
  const [chartWidth, setChartWidth] = useState(0);
  const [chartHeight, setChartHeight] = useState(0);
  useEffect(() => {
    if (!chartCanvasRef.current) return undefined;
    const node = chartCanvasRef.current;
    let timeoutId = null;
    const measure = () => {
      setChartWidth(node.clientWidth || 0);
      setChartHeight(node.clientHeight || 0);
    };
    const throttledMeasure = () => {
      if (timeoutId) return; // ja agendado — descarta
      timeoutId = setTimeout(() => {
        timeoutId = null;
        measure();
      }, 150);
    };
    // Primeira medida imediata (operador nao espera 150ms pra primeiro
    // render). Subsequentes throttled.
    measure();
    if (typeof ResizeObserver !== 'undefined') {
      const ro = new ResizeObserver(throttledMeasure);
      ro.observe(node);
      return () => {
        ro.disconnect();
        if (timeoutId) clearTimeout(timeoutId);
      };
    }
    // Fallback pra browsers antigos sem ResizeObserver
    window.addEventListener('resize', throttledMeasure);
    return () => {
      window.removeEventListener('resize', throttledMeasure);
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, []);
  useEffect(() => {
    if (!switcherOpen) return undefined;
    const handler = (e) => {
      if (switcherRef.current && !switcherRef.current.contains(e.target)) {
        setSwitcherOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [switcherOpen]);

  useEffect(() => {
    if (!thingId) {
      setEvents([]);
      return undefined;
    }
    let mounted = true;
    Promise.all([
      getThingTrips(thingId, 1, 100).catch(() => ({ data: [] })),
      getThingBlocks(thingId, 1, 100).catch(() => ({ data: [] })),
    ]).then(([tripsResp, blocksResp]) => {
      if (!mounted) return;
      const flatten = (resp) => {
        const raw = (resp && resp.data) || [];
        if (Array.isArray(raw)) return raw;
        if (Array.isArray(raw.items)) return raw.items;
        return [];
      };
      const parseTime = (e) => {
        const candidates = [e.createdAt, e.ts, e.timestamp, e.date];
        let found = null;
        candidates.some((c) => {
          if (!c) return false;
          const t =
            typeof c === 'number'
              ? c < 1e12 ? c * 1000 : c
              : new Date(c).getTime();
          if (Number.isFinite(t)) {
            found = t;
            return true;
          }
          return false;
        });
        return found;
      };
      const tripsList = flatten(tripsResp).map((t) => ({
        ts: parseTime(t),
        type: 'trip',
        reason: t.reason || t.description || 'Trip',
      }));
      const blocksList = flatten(blocksResp).map((b) => ({
        ts: parseTime(b),
        type: 'block',
        reason: b.reason || b.description || 'Block',
      }));
      setEvents(
        [...tripsList, ...blocksList].filter((e) => Number.isFinite(e.ts))
      );
    });
    return () => {
      mounted = false;
    };
  }, [thingId]);

  const updateGraph = (values) => {
    if (setLoadingGraph) setLoadingGraph(true);
    const tmpVars = values.map((vlr) => ({
      ...vlr,
      value: parseFloat(vlr.value),
    }));
    // Range do Y axis virou memo (yAxisRange) — depende do modo e do
    // toggle de suavizacao, entao precisa ser reativo (recomputa quando
    // o operador liga/desliga 'suavizar'). Aqui so guardamos os dados.
    setData(tmpVars);
    if (setLoadingGraph) setLoadingGraph(false);
  };

  // Ticks do X em fronteiras redondas (15min/30min/1h/.../1dia/etc.),
  // com densidade RESPONSIVA pela largura disponivel. Subtraimos a
  // largura do YAxis (~70px) + margem direita (~20px) pra estimar a
  // area util do plot. Quando o chart e estreito (sidebar aberta,
  // split-view, etc.), o algoritmo pega step maior automaticamente
  // pros labels nao se sobreporem/cortarem.
  // xAxisTicks: declarado adiante (apos `displayData`), porque depende
  // da serie ja downsamplada que e renderizada pelo Recharts.

  // REMOVIDO: useEffect que dispatchava HISTORIC/RESET_HISTORIC em
  // toda mudanca de selectedVar/period/thing. Esse reset era hammer
  // demais: limpava TODO o cache de Redux historic, inclusive de
  // outras variaveis em uso. Pior: quando o modal de analise avancada
  // monta um SEGUNDO LineChart, o reset disparava e StatsPanel (que le
  // direto do Redux) ficava com data=[] eternamente. O reducer ja
  // sobrescreve por variavel+period quando a saga responde — cache
  // antigo ate beneficia (cache hit ao alternar Mes/Semana/Dia).
  // Limpeza global continua acontecendo no unmount do Produto
  // (aplicacao.js dispatcha HISTORIC/RESET).

  useEffect(() => {
    if (variableHistoric && selectedPeriod && variableHistoric[selectedPeriod]) {
      updateGraph(variableHistoric[selectedPeriod]);
    }
    // O efeito reage exclusivamente a updates de `variableHistoric`;
    // `selectedPeriod`/`updateGraph` sao lidos mas nao devem disparar
    // re-execucao (ja tratados pelo effect anterior que reseta historico).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [variableHistoric]);

  // Modo simulador (so dev): sem historico real, injeta uma serie fake pro
  // grafico pra visualizar o layout (ex.: bluebox sem ingestao). Dado real
  // sempre vence. Desligado + sem real => vazio ("nao ha dados").
  const { enabled: simulatorEnabled } = useSimulator();
  useEffect(() => {
    const real =
      variableHistoric && selectedPeriod && variableHistoric[selectedPeriod];
    const hasReal = Array.isArray(real) && real.length > 0;
    if (hasReal) return; // real e tratado pelo effect acima
    if (simulatorEnabled && selectedVar && selectedVar.ihmname) {
      updateGraph(buildSimulatedSeries(selectedVar, selectedPeriod));
    } else {
      setData([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [simulatorEnabled, variableHistoric, selectedPeriod, selectedVar]);

  // Busca dados do periodo anterior (ghost line) quando a aba `showGhost`
  // esta ativa. Usa o param `date=` do backend (commit 593680f) — janela
  // termina em (now - 1 intervalo). Exemplo daily: ghost cobre [now-48h,
  // now-24h]. Apos load, o merge no useMemo abaixo encaixa cada ponto
  // ghost no ponto atual correspondente por offset temporal.
  useEffect(() => {
    if (!showGhost) {
      setGhostData([]);
      return undefined;
    }
    if (!selectedVar || !selectedVar.ihmname || !thing) return undefined;
    const fetcher = fetchFnFor[selectedPeriod];
    if (!fetcher) return undefined;
    const offsetMs =
      (PERIOD_INTERVAL_DAYS[selectedPeriod] || 1) * DAY_MS;
    const ghostEndDate = new Date(Date.now() - offsetMs).toISOString();
    const thingName =
      (thing && thing.thing) || (Array.isArray(thing) && thing[0]?.thing);
    if (!thingName) return undefined;
    let mounted = true;
    fetcher(selectedVar.ihmname, thingName, ghostEndDate)
      .then((resp) => {
        if (!mounted) return;
        // Mesma transformacao de updateGraph mas sem mexer no yAxisRange
        // (ghost nao deve forcar dominio Y; segue dominio da curva atual).
        const rows = (resp && resp.data) || [];
        const parsed = rows
          .map((r) => ({
            timestamp:
              typeof r.timestamp === 'number'
                ? r.timestamp
                : parseFloat(r.timestamp),
            value: parseFloat(r.value),
          }))
          .filter((p) => Number.isFinite(p.value) && Number.isFinite(p.timestamp));
        setGhostData(parsed);
      })
      .catch(() => {
        if (mounted) setGhostData([]);
      });
    return () => {
      mounted = false;
    };
  }, [showGhost, selectedVar, selectedPeriod, thing]);
  const unit =
    (selectedVar.range && selectedVar.range[0] && selectedVar.range[0].unit) ||
    selectedVar.unit ||
    '';

  const chartData = useMemo(() => {
    if (
      !data.length ||
      !selectedVar ||
      !selectedVar.ihmname ||
      !realtimeThingData
    ) {
      return data;
    }

    const liveValue = parseFloat(realtimeThingData[selectedVar.ihmname]);
    const liveTsMs = parseTimestampMs(realtimeThingData.ts);
    const lastHistoricTsMs = parseTimestampMs(data[data.length - 1].timestamp);

    if (
      !Number.isFinite(liveValue) ||
      !liveTsMs ||
      !lastHistoricTsMs ||
      liveTsMs - lastHistoricTsMs < 60 * 1000
    ) {
      return data;
    }

    return [
      ...data,
      {
        ...data[data.length - 1],
        timestamp: liveTsMs / 1000,
        value: liveValue,
        isRealtime: true,
      },
    ];
  }, [data, realtimeThingData, selectedVar]);

  // Ranges da variavel (ex.: Normal 0-10, Atencao 10-20, Erro 20+). Pegamos
  // os limites superiores como reference lines coloridas no grafico — assim
  // o operador ve historicamente quando o valor cruzou para zona de atencao.
  // Memoizado: cada render criava novo array (reference muda),
  // invalidando todos os useMemo descendentes (dataStatsRaw, gradient,
  // etc.) sem motivo. Agora so recalcula quando o range da variavel
  // realmente mudar.
  const referenceLines = useMemo(
    () =>
      (selectedVar.range || [])
        .filter(
          (r) => r.category && r.category !== 'NA' && Number.isFinite(Number(r.max))
        )
        .map((r) => ({
          value: Number(r.max),
          color: GetColor(r.category, 'ranges') || colors.label,
          label: r.category,
        })),
    [selectedVar.range, colors.label]
  );

  // Range bruto do Y axis (min/max usados pelo yAxisConfig). Reativo a
  // chartData + modo + toggle de suavizacao:
  //   - Avancado BRUTO (suavizar OFF): range COMPLETO (min/max reais) —
  //     nenhum pico/vale cortado na borda (resolve "escala curta" da
  //     analise fina). Loop em vez de Math.min(...spread) pra aguentar
  //     series grandes (mensal) sem estourar a call stack.
  //   - Basico e Avancado SUAVIZADO: percentil 2-98 — outliers de sensor
  //     nao esmagam a operacao normal; com a linha suavizada o chart fica
  //     limpo e preenchido (mesma leitura agradavel do basico).
  const yAxisRange = useMemo(() => {
    const finiteValues = [];
    for (let i = 0; i < chartData.length; i += 1) {
      const v = Number(chartData[i].value);
      if (Number.isFinite(v)) finiteValues.push(v);
    }
    if (finiteValues.length === 0) return { min: 0, max: 0 };
    const rawAdvanced = isAdvanced && !smoothAdvanced;
    if (rawAdvanced || finiteValues.length < 10) {
      let lo = Infinity;
      let hi = -Infinity;
      for (let i = 0; i < finiteValues.length; i += 1) {
        if (finiteValues[i] < lo) lo = finiteValues[i];
        if (finiteValues[i] > hi) hi = finiteValues[i];
      }
      return { min: lo, max: hi };
    }
    const sorted = [...finiteValues].sort((a, b) => a - b);
    const pIdx = (p) =>
      Math.min(sorted.length - 1, Math.max(0, Math.floor(sorted.length * p)));
    let lo = sorted[pIdx(0.02)];
    let hi = sorted[pIdx(0.98)];
    if (lo === hi) {
      [lo] = sorted;
      hi = sorted[sorted.length - 1];
    }
    return { min: lo, max: hi };
  }, [chartData, isAdvanced, smoothAdvanced]);

  // Domain do Y axis (computado uma vez via memo pra reaproveitar em:
  //   1. <YAxis domain={...} ticks={...}>
  //   2. <linearGradient stops do statusGradient>
  // Antes era recalculado 2x inline no JSX, que ja era caro (loop pra
  // gerar nice numbers) e agora seria 3x com o gradient.
  const yAxisConfig = useMemo(
    () => {
      // Modo basico tem grid mais leve (5 linhas / 6 ticks) pra
      // chart parecer leve no glance. Avancado mantem 10/11 pra
      // resolucao fina na analise.
      const lines = isAdvanced ? Y_AXIS_LINES : 5;
      return evenlyDistributedTicks(yAxisRange.min, yAxisRange.max, lines);
    },
    [yAxisRange.min, yAxisRange.max, isAdvanced]
  );

  // Stops do gradient vertical pra linha colorida por zona de status:
  // verde no zone Normal, laranja no Warning, vermelho no Erro. Mapeamos
  // cada threshold (referenceLines.value) pra um offset no gradient
  // SVG, onde offset 0% = topo do chart (max Y) e 100% = base (min Y).
  // Dois stops no mesmo offset criam transicao "sharp" (cor muda
  // bruscamente no threshold, sem blend) — desejado pra zona de status
  // ser clara visualmente em vez de virar gradiente continuo.
  const statusGradientStops = useMemo(() => {
    if (!referenceLines.length || !yAxisConfig) return null;
    const [niceMin, niceMax] = yAxisConfig.domain;
    if (niceMin === niceMax) return null;
    const sorted = [...referenceLines].sort((a, b) => a.value - b.value);
    // Cor acima do threshold mais alto = cor do proprio threshold (ex.:
    // se valor passa do Erro.max, ainda exibimos como Erro/vermelho).
    const topColor = sorted[sorted.length - 1].color;
    const stops = [{ offset: 0, color: topColor }];
    for (let i = sorted.length - 1; i >= 0; i -= 1) {
      const threshold = sorted[i].value;
      const offset = Math.max(
        0,
        Math.min(100, ((niceMax - threshold) / (niceMax - niceMin)) * 100)
      );
      const colorAbove =
        i === sorted.length - 1 ? topColor : sorted[i + 1].color;
      const colorBelow = sorted[i].color;
      // Stop par no mesmo offset = transicao sharp
      stops.push({ offset, color: colorAbove });
      stops.push({ offset, color: colorBelow });
    }
    // Base do gradient: cor da zona mais baixa (Normal)
    stops.push({ offset: 100, color: sorted[0].color });
    return stops;
  }, [referenceLines, yAxisConfig]);

  // Stats agregadas do periodo atual: min, max, media. Renderizadas no
  // header como contexto numerico — operador entende variabilidade sem
  // precisar ler a curva inteira.
  const dataStats = useMemo(() => {
    if (!chartData.length) return null;
    const values = chartData
      .map((p) => Number(p.value))
      .filter((v) => Number.isFinite(v));
    if (values.length === 0) return null;
    let sum = 0;
    let min = Infinity;
    let max = -Infinity;
    values.forEach((v) => {
      sum += v;
      if (v < min) min = v;
      if (v > max) max = v;
    });
    return {
      min,
      max,
      avg: sum / values.length,
      count: values.length,
    };
  }, [chartData]);

  // Stats COMPLETOS (calcChartStats) — usado pra trend line overlay no
  // chart em modo avancado. Quando precomputedStats vem do parent
  // (aplicacao modal), reusa direto — evita rodar 2x. Senao calcula
  // internamente. Em modo basico nao usamos (skip pra performance).
  const dataStatsRaw = useMemo(() => {
    if (precomputedStats !== undefined) return precomputedStats;
    return isAdvanced ? calcChartStats(chartData, referenceLines) : null;
  }, [precomputedStats, chartData, referenceLines, isAdvanced]);

  // Smoothing por media movel — usado em modo basico pra reduzir ruido
  // de alta frequencia (a operacao normal de uma UCHA oscila 1-2 L/s a
  // cada amostra; isso polui visualmente sem agregar info pra glance).
  // window dinamico: ~2% do total de pontos, minimo 5. Para 24h com
  // 288 amostras (5min interval) -> window=5 (~25min de suavizacao).
  // Pra 1000 amostras (raw 1min) -> window=20 (~20min). Sempre par+1
  // pra ter centro definido.
  const smoothedData = useMemo(() => {
    if (!chartData.length) return chartData;
    // Basico sempre suaviza. Avancado suaviza so com o toggle 'suavizar'
    // ligado (default ON) — desligado mostra o dado bruto pra analise fina.
    if (isAdvanced && !smoothAdvanced) return chartData;
    // Series muito curtas (< 100 pts) nao se beneficiam de smoothing — o
    // window minimo (5) ja seria ~5% do total e suaviza demais. Tambem
    // economiza um O(n*w) na primeira renderizacao do grafico.
    if (chartData.length < 100) return chartData;
    const windowSize = Math.max(5, Math.floor(chartData.length / 50));
    // Garante impar (centro definido)
    const w = windowSize % 2 === 0 ? windowSize + 1 : windowSize;
    const half = Math.floor(w / 2);
    return chartData.map((point, i) => {
      let sum = 0;
      let count = 0;
      const start = Math.max(0, i - half);
      const end = Math.min(chartData.length - 1, i + half);
      for (let j = start; j <= end; j += 1) {
        const v = Number(chartData[j].value);
        if (Number.isFinite(v)) {
          sum += v;
          count += 1;
        }
      }
      return {
        ...point,
        value: count > 0 ? sum / count : point.value,
      };
    });
  }, [chartData, isAdvanced, smoothAdvanced]);

  // Merge: cada ponto da curva atual ganha um campo `ghostValue` com o
  // valor da curva anterior no mesmo "horario equivalente" (timestamp atual
  // - offset do periodo). Recharts renderiza duas Lines no mesmo grid usando
  // o array unificado. Em modo basico usa smoothedData; avancado usa raw.
  const mergedData = useMemo(() => {
    const baseData = smoothedData;
    if (!baseData.length) return [];
    if (!showGhost || !ghostData.length) return baseData;
    const offsetMs =
      (PERIOD_INTERVAL_DAYS[selectedPeriod] || 1) * DAY_MS;
    // Sort ghost por timestamp pra busca eficiente (linear scan ainda — mas
    // como esta ordenado, paramos cedo).
    const sortedGhost = [...ghostData].sort((a, b) => a.timestamp - b.timestamp);
    return baseData.map((point) => {
      const targetMs = point.timestamp * 1000 - offsetMs;
      // Busca o ponto ghost cujo timestamp esta mais proximo do alvo. Como
      // sortedGhost e ordenado, paramos no primeiro cuja diferenca comeca a
      // crescer.
      let best = null;
      let bestDiff = Infinity;
      for (let i = 0; i < sortedGhost.length; i += 1) {
        const gMs = sortedGhost[i].timestamp * 1000;
        const diff = Math.abs(gMs - targetMs);
        if (diff < bestDiff) {
          bestDiff = diff;
          best = sortedGhost[i];
        } else if (gMs > targetMs) {
          break; // ja passamos do alvo, nao adianta continuar
        }
      }
      // Toleramos diferenca de ate 1h pra considerar match (evita encaixar
      // ponto muito distante).
      const tolerance = 60 * 60 * 1000;
      return {
        ...point,
        ghostValue:
          best && bestDiff < tolerance ? best.value : undefined,
      };
    });
  }, [smoothedData, ghostData, showGhost, selectedPeriod]);

  // Merge das compareSeries no mergedData — cada ponto da curva
  // primaria ganha um campo extra com o valor de cada compareVar no
  // mesmo timestamp (mais proximo). Recharts renderiza N Lines em
  // paralelo com dataKey={ihmname} pra cada serie. Tolerancia de 1h
  // pra match evita encaixar pontos distantes quando as series tem
  // sampling-rate diferente.
  //
  // Otimizacao two-pointer: mergedData e cada series.data ja vem
  // ORDENADOS por timestamp ascendente, entao para cada nova serie
  // mantemos um ponteiro que so AVANCA. Complexidade O(K*(N+M)) em vez
  // do O(N*M*K) anterior (busca linear restartando do zero pra cada
  // ponto). Pra janela mensal (~40k pontos x 3 series), e a diferenca
  // entre ~5s e ~50ms — o gargalo principal do "add compare variable".
  const multiSeriesData = useMemo(() => {
    if (!compareSeries.length || !mergedData.length) return mergedData;
    const TOLERANCE_MS = 60 * 60 * 1000;
    // Pre-computa timestamps em ms (Number * 1000) uma vez por serie,
    // evitando reconverter dentro do loop O(N*M*K) antigo.
    const seriesState = compareSeries.map((series) => ({
      series,
      times:
        series.data && series.data.length
          ? series.data.map((d) => Number(d.timestamp) * 1000)
          : [],
      ptr: 0,
    }));
    return mergedData.map((point) => {
      const enriched = { ...point };
      const targetMs = point.timestamp * 1000;
      for (let s = 0; s < seriesState.length; s += 1) {
        const state = seriesState[s];
        if (state.times.length) {
          // Avanca ptr enquanto o proximo ponto estiver MAIS PROXIMO do
          // target que o atual. Como targetMs cresce monotonicamente
          // (mergedData ordenado), ptr nunca retrocede — amortizado O(M)
          // por serie ao longo de todo o map.
          while (
            state.ptr + 1 < state.times.length &&
            Math.abs(state.times[state.ptr + 1] - targetMs) <
              Math.abs(state.times[state.ptr] - targetMs)
          ) {
            state.ptr += 1;
          }
          const diff = Math.abs(state.times[state.ptr] - targetMs);
          if (diff < TOLERANCE_MS) {
            const v = Number(state.series.data[state.ptr].value);
            enriched[state.series.ihmname] = Number.isFinite(v)
              ? v
              : undefined;
          }
        }
      }
      return enriched;
    });
  }, [mergedData, compareSeries]);

  // Downsampling LTTB: aplica so quando a serie passa do threshold
  // (~2000 pontos). Algoritmo mantem picos/vales/transicoes — visual
  // indistinguivel da curva raw em <800px de largura, com 5-10x menos
  // pontos pro SVG renderizar. Stats e outliers continuam usando RAW
  // (data) — downsampling muda a distribuicao.
  const displayData = useMemo(() => {
    if (multiSeriesData.length <= DOWNSAMPLE_THRESHOLD) return multiSeriesData;
    return downsampleLTTB(multiSeriesData, DOWNSAMPLE_TARGET);
  }, [multiSeriesData]);

  // displayData (nao data) — devolve indices DENTRO do array renderizado
  // pelo Recharts. Quando downsampling ativo, displayData tem menos
  // pontos que data, e roundedTimeIndices encontra os melhores indices
  // no array exibido. Memoizado pra nao reexecutar roundedTimeIndices a
  // cada render disparado por subscription tick.
  const xAxisTicks = useMemo(() => {
    if (!displayData.length || !chartWidth) return undefined;
    const plotWidthPx = Math.max(200, chartWidth - 90);
    // Modo basico: queremos "1h quando couber, senao adapta". Usamos a
    // largura cheia do plot (sem reduzir) + piso de 1h — assim o eixo
    // mostra 1 em 1 hora quando os labels cabem e sobe pra 2h/3h/6h
    // automaticamente em telas estreitas (sem sobrepor). O piso evita
    // cair pra 15/30min em monitores largos.
    // Modo avancado: densidade total (steps de 15min/30min liberados)
    // pra resolucao temporal fina na analise.
    const MIN_STEP_BASIC_MS = 60 * 60 * 1000; // 1h
    return roundedTimeIndices(
      displayData,
      plotWidthPx,
      isAdvanced ? 0 : MIN_STEP_BASIC_MS,
    );
  }, [displayData, chartWidth, isAdvanced]);

  // Emite onZoomChange com TIMESTAMPS (nao indices) sempre que
  // brushRange muda. Parent (modal) usa pra filtrar advancedData e
  // recalcular stats sobre a janela visivel. Indices nao servem
  // pra parent pq displayData e estado interno do LineChart.
  useEffect(() => {
    if (!onZoomChange) return;
    if (!brushRange || !displayData.length) {
      onZoomChange(null);
      pendingZoomYRef.current = null;
      return;
    }
    const start = displayData[brushRange.startIndex];
    const end = displayData[brushRange.endIndex];
    if (start && end) {
      const yPart = pendingZoomYRef.current;
      pendingZoomYRef.current = null; // consume
      onZoomChange({
        startTs: start.timestamp,
        endTs: end.timestamp,
        ...(yPart && { yMin: yPart.yMin, yMax: yPart.yMax }),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brushRange, displayData]);

  // Domain de cada compareSeries — calculado uma vez por mudanca em
  // compareSeries pra manter Y axis secundario com escala robusta.
  // Usa percentil 2-98 (mesma logica da primaria) mas via array de
  // values cru de cada serie.
  const compareYDomains = useMemo(() => {
    const domains = {};
    compareSeries.forEach((series) => {
      if (!series.data || !series.data.length) {
        domains[series.ihmname] = [0, 1];
        return;
      }
      const values = series.data
        .map((p) => Number(p.value))
        .filter((v) => Number.isFinite(v))
        .sort((a, b) => a - b);
      if (!values.length) {
        domains[series.ihmname] = [0, 1];
        return;
      }
      const pIdx = (p) =>
        Math.min(values.length - 1, Math.max(0, Math.floor(values.length * p)));
      const lo = values[pIdx(0.02)];
      const hi = values[pIdx(0.98)];
      const padding = (hi - lo) * 0.1 || 1;
      domains[series.ihmname] = [lo - padding, hi + padding];
    });
    return domains;
  }, [compareSeries]);

  // Memos finos pra Recharts nao re-renderizar tudo a cada parent
  // render. Antes esses 2 objetos eram criados inline no JSX
  // (novo objeto = nova ref = props.changed pro Recharts = re-render
  // do Line + ReferenceLine). Com dataset 10k+ pontos, isso somava
  // segundos de jank.

  // dot function da Line principal. Closure captura outlierTimestamps
  // (Set imutavel vindo do calcChartStats memoizado). Inline anterior
  // recriava a funcao a cada render — Recharts re-chamava pra todos
  // os N pontos.
  const dotRenderer = useMemo(
    () =>
      makeCustomizedDot(
        colors.darkblue,
        colors.red,
        isAdvanced && dataStatsRaw ? dataStatsRaw.outlierTimestamps : null
      ),
    [colors.darkblue, colors.red, isAdvanced, dataStatsRaw]
  );

  // Segment da trend line — array com 2 pontos. Recharts compara
  // reference, inline recriava sempre.
  // Segmentos operacionais (stopped) mapeados pra indices em
  // displayData. Renderizamos so os 'stopped' como bandas — running
  // fica transparente (presume-se default visual). Filtra os fora da
  // janela visivel.
  const operationalStoppedBands = useMemo(() => {
    if (
      !isAdvanced ||
      !operationalSegments ||
      !operationalSegments.length ||
      !displayData.length
    ) {
      return [];
    }
    const firstTs = displayData[0].timestamp;
    const lastTs = displayData[displayData.length - 1].timestamp;
    return operationalSegments
      .filter((seg) => seg.state === 'stopped')
      .filter((seg) => seg.endTs >= firstTs && seg.startTs <= lastTs)
      .map((seg) => {
        // Acha indice mais proximo de startTs e endTs em displayData.
        // Clampa nas bordas visiveis.
        let startIdx = 0;
        let endIdx = displayData.length - 1;
        let bestStartDiff = Infinity;
        let bestEndDiff = Infinity;
        const clampedStart = Math.max(seg.startTs, firstTs);
        const clampedEnd = Math.min(seg.endTs, lastTs);
        for (let i = 0; i < displayData.length; i += 1) {
          const dStart = Math.abs(displayData[i].timestamp - clampedStart);
          if (dStart < bestStartDiff) {
            bestStartDiff = dStart;
            startIdx = i;
          }
          const dEnd = Math.abs(displayData[i].timestamp - clampedEnd);
          if (dEnd < bestEndDiff) {
            bestEndDiff = dEnd;
            endIdx = i;
          }
        }
        return {
          startIdx,
          endIdx,
          key: `${seg.startTs}-${seg.endTs}`,
        };
      });
  }, [isAdvanced, operationalSegments, displayData]);

  // Anotacoes mapeadas pra indices em displayData (mesma logica de
  // change-points/eventReferenceLines). Filtra as fora do range.
  const annotationRefs = useMemo(() => {
    if (!isAdvanced || !annotations || !annotations.length || !displayData.length) {
      return [];
    }
    const firstTs = displayData[0].timestamp;
    const lastTs = displayData[displayData.length - 1].timestamp;
    return annotations
      .filter((a) => a.timestamp >= firstTs && a.timestamp <= lastTs)
      .map((a) => {
        let bestIdx = 0;
        let bestDiff = Infinity;
        for (let i = 0; i < displayData.length; i += 1) {
          const d = Math.abs(displayData[i].timestamp - a.timestamp);
          if (d < bestDiff) {
            bestDiff = d;
            bestIdx = i;
          } else if (displayData[i].timestamp > a.timestamp) {
            break;
          }
        }
        return {
          x: bestIdx,
          text: a.text,
          id: a.id,
        };
      });
  }, [isAdvanced, annotations, displayData]);

  // Change-points mapeados pra indices em displayData. Os change-points
  // vem do calcChartStats (rodado sobre data raw), entao guardam
  // timestamps. Pra renderizar como ReferenceLine vertical no chart
  // (que usa indices de displayData), achamos o indice mais proximo
  // de cada timestamp. Filtra os fora do range visivel.
  const changePointRefs = useMemo(() => {
    if (!isAdvanced || !dataStatsRaw || !dataStatsRaw.changePoints || !displayData.length) {
      return [];
    }
    if (dataStatsRaw.changePoints.length === 0) return [];
    const firstTs = displayData[0].timestamp;
    const lastTs = displayData[displayData.length - 1].timestamp;
    return dataStatsRaw.changePoints
      .filter((cp) => cp.timestamp >= firstTs && cp.timestamp <= lastTs)
      .map((cp) => {
        let bestIdx = 0;
        let bestDiff = Infinity;
        for (let i = 0; i < displayData.length; i += 1) {
          const d = Math.abs(displayData[i].timestamp - cp.timestamp);
          if (d < bestDiff) {
            bestDiff = d;
            bestIdx = i;
          } else if (displayData[i].timestamp > cp.timestamp) {
            break;
          }
        }
        return {
          x: bestIdx,
          delta: cp.delta,
          significance: cp.significance,
        };
      });
  }, [isAdvanced, dataStatsRaw, displayData]);

  const trendSegment = useMemo(() => {
    // x usa displayData.length (nao multiSeriesData) pq trend renderiza
    // SOBRE o array exibido. LTTB preserva primeiro/ultimo points,
    // entao trend.startValue / endValue continuam validos nos extremos.
    if (
      !isAdvanced ||
      !dataStatsRaw ||
      !dataStatsRaw.trend ||
      displayData.length < 2
    ) {
      return null;
    }
    return [
      { x: 0, y: dataStatsRaw.trend.startValue },
      { x: displayData.length - 1, y: dataStatsRaw.trend.endValue },
    ];
  }, [isAdvanced, dataStatsRaw, displayData.length]);

  // Mapeia eventos (trips/blocks) para indices no array de dados — o chart
  // posiciona ReferenceLine vertical via x={index}. Pegamos o ponto cujo
  // timestamp e mais proximo do evento. Se o evento estiver fora do range
  // exibido, descartamos.
  const eventReferenceLines = useMemo(() => {
    // displayData (nao data): o x do ReferenceLine vertical e o INDICE
    // dentro do array RENDERIZADO. Com downsampling, indices em `data`
    // nao mapeiam pro chart. displayData preserva primeiro/ultimo
    // pontos (LTTB garantia), entao o range temporal e identico.
    if (!displayData.length || !events.length) return [];
    const firstTs = displayData[0].timestamp * 1000;
    const lastTs = displayData[displayData.length - 1].timestamp * 1000;
    return events
      .filter((e) => e.ts >= firstTs && e.ts <= lastTs)
      .map((e) => {
        // busca linear simples — displayData ordenado por timestamp
        let bestIdx = 0;
        let bestDiff = Infinity;
        for (let i = 0; i < displayData.length; i += 1) {
          const diff = Math.abs(displayData[i].timestamp * 1000 - e.ts);
          if (diff < bestDiff) {
            bestDiff = diff;
            bestIdx = i;
          }
        }
        return {
          x: bestIdx,
          color: e.type === 'block' ? colors.red : colors.yellow,
          // Glyphs unicode geometricos em vez dos emojis ⛔/⚠ que
          // renderizavam diferente por SO/browser. Recharts ReferenceLine
          // label aceita so string — SVG inline aqui exige custom label
          // component. Mantemos texto simples com semantica clara:
          //   block → "B" (bloqueio)
          //   trip  → "!" (alerta/trip)
          // Cor (red/yellow) ja diferencia visualmente.
          label: e.type === 'block' ? 'B' : '!',
          reason: e.reason,
        };
      });
  }, [displayData, events, colors]);

  // #5 Marcador do evento selecionado no Log — mesmo mapeamento ts->indice
  // dos eventReferenceLines, mas pra UM instante (focusTs) e com destaque
  // visual proprio. Retorna o indice (x) ou null quando o evento esta fora
  // da janela exibida.
  const focusReferenceX = useMemo(() => {
    if (!focusTs || !displayData.length) return null;
    const firstTs = displayData[0].timestamp * 1000;
    const lastTs = displayData[displayData.length - 1].timestamp * 1000;
    if (focusTs < firstTs || focusTs > lastTs) return null;
    let bestIdx = 0;
    let bestDiff = Infinity;
    for (let i = 0; i < displayData.length; i += 1) {
      const diff = Math.abs(displayData[i].timestamp * 1000 - focusTs);
      if (diff < bestDiff) {
        bestDiff = diff;
        bestIdx = i;
      }
    }
    return bestIdx;
  }, [focusTs, displayData]);

  // Ultima atualizacao por extenso: data + hora ('d/L/y - HH:mm').
  // Antes mostrava so 'HH:mm' por causa do header apertado, mas o
  // operador pediu data/hora completas pra ter contexto de quanto
  // tempo atras foi a leitura (ex.: '19/05/2026 - 10:21' vs '10:21'
  // que pode confundir entre dias).
  const lastUpdateMs = parseTimestampMs(lastUpdateTs);
  const lastUpdateRaw = lastUpdateMs ? new Date(lastUpdateMs) : null;
  const lastUpdate = lastUpdateRaw
    ? format(lastUpdateRaw, dateFormats.DATE_TIME_SLASH)
    : null;
  const lastUpdateFull = lastUpdate;

  // Live indicator: dot pulsando se a ultima atualizacao tem < 60s
  // (subscription provavelmente ativa). Sem isso o operador nao sabe
  // se a curva esta atualizando ou se ta congelada.
  const isLive =
    lastUpdateRaw && Date.now() - lastUpdateRaw.getTime() < 60 * 1000;

  // Formatador local pra stats (Min/Max/Avg). Reaproveita unit do
  // selectedVar pra deixar claro a escala.
  const formatStat = (v) => {
    if (!Number.isFinite(v)) return '—';
    // Notacao mais legivel: arredonda pra 2 casas em valores < 100,
    // 1 casa em 100-1000, inteiro acima disso.
    if (Math.abs(v) < 100) return v.toFixed(2);
    if (Math.abs(v) < 1000) return v.toFixed(1);
    return Math.round(v).toString();
  };

  // Filtra availableVariables pra mostrar so as que tem range valido
  // (mesma logica do velocimetro — descarta variaveis 'NA').
  const switcherOptions = (availableVariables || []).filter(
    (v) => v && v.ihmname && v.name
  );

  const handlePickVariable = (variable) => {
    setSwitcherOpen(false);
    if (setSelectedVar && variable.id !== selectedVar.id) {
      setSelectedVar(variable);
    }
  };

  return (
    <s.Container id="history-graph-container">
      <s.TagContainer>
        <span ref={switcherRef} style={{ position: 'relative' }}>
          Histórico &nbsp;
          {/* Quick switcher: clique no nome abre dropdown rapido com a
              lista de variaveis disponiveis pra essa thing. Operador troca
              a curva sem precisar voltar ao Velocimetro pra clicar no
              gauge. So aparece em modo avancado — em modo basico
              mostramos o nome em h3 (sem interacao) pra header limpo. */}
          {isAdvanced && setSelectedVar && switcherOptions.length > 0 ? (
            <s.SwitcherButton
              type="button"
              onClick={() => setSwitcherOpen((v) => !v)}
              aria-expanded={switcherOpen}
              title="Trocar variável"
            >
              {selectedVar.name}
              <s.SwitcherChevron $open={switcherOpen}>▾</s.SwitcherChevron>
            </s.SwitcherButton>
          ) : (
            <h3>{selectedVar.name}</h3>
          )}
          {isAdvanced && switcherOpen && (
            <s.SwitcherDropdown>
              {switcherOptions.map((variable) => (
                <s.SwitcherOption
                  key={variable.id}
                  type="button"
                  $selected={selectedVar.id === variable.id}
                  onClick={() => handlePickVariable(variable)}
                >
                  {variable.name}
                </s.SwitcherOption>
              ))}
            </s.SwitcherDropdown>
          )}
        </span>
        <s.PeriodSegmented>
          {periods.map(({ label, value }) => (
            <s.Tag
              key={label}
              onClick={() => setSelectedPeriod(value)}
              color={colors.white}
              selected={value === selectedPeriod}
            >
              {label}
            </s.Tag>
          ))}
        </s.PeriodSegmented>
        {/* Toggle 'suavizar' (so avancado): liga/desliga a media movel.
            Default ON pra leitura agradavel (igual ao basico). Desligado
            mostra o dado bruto + eixo Y completo (analise fina de ruido). */}
        {isAdvanced && (
          <s.GhostToggle
            type="button"
            onClick={() => setSmoothAdvanced((v) => !v)}
            $active={smoothAdvanced}
            title={
              smoothAdvanced
                ? 'Mostrar dado bruto (sem suavizar)'
                : 'Suavizar ruído (média móvel)'
            }
          >
            suavizar
          </s.GhostToggle>
        )}
        {/* Toggle ghost line: chip standalone (nao wrapper segmented) pra
            economizar espaco no header e deixar visualmente claro que e
            controle distinto. So em modo avancado — comparativo
            temporal e funcao de analise, nao de glance. */}
        {isAdvanced && (
          <s.GhostToggle
            type="button"
            onClick={() => setShowGhost((v) => !v)}
            $active={showGhost}
            title={
              selectedPeriod === 'daily'
                ? 'Comparar com ontem'
                : selectedPeriod === 'weekly'
                ? 'Comparar com a semana anterior'
                : 'Comparar com o mês anterior'
            }
          >
            vs anterior
          </s.GhostToggle>
        )}
        {/* Botao 'Modo avancado' — so aparece quando o consumidor passa
            onRequestAdvanced (tela decide o que fazer ao clicar, normalmente
            abrir modal fullscreen com mode='advanced'). Em mode='advanced'
            o botao tambem nao aparece (ja esta em modo avancado). */}
        {!isAdvanced && onRequestAdvanced && (
          <s.AdvancedToggle
            type="button"
            onClick={onRequestAdvanced}
            // So habilita quando NAO esta carregando E ja tem dados —
            // antes o operador podia clicar com o chart vazio e abrir
            // o modal sem nada pra analisar. Tooltip muda conforme o estado.
            disabled={loadingGraph || !data || data.length === 0}
            title={
              loadingGraph
                ? 'Aguarde — carregando dados…'
                : !data || data.length === 0
                ? 'Sem dados no período selecionado'
                : 'Abrir modo avançado (análise completa, multi-variável, export)'
            }
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <polyline points="15 3 21 3 21 9" />
              <polyline points="9 21 3 21 3 15" />
              <line x1="21" y1="3" x2="14" y2="10" />
              <line x1="3" y1="21" x2="10" y2="14" />
            </svg>
            avançado
          </s.AdvancedToggle>
        )}
      </s.TagContainer>

      {chartType === 'bool' ? (
        <>
          <s.TimelineWrapper>
            <h3>Primeiro Erro</h3>
            <s.Timeline>
              {[1, 2, 3].map(() => (
                <s.TimelineItem>15:35</s.TimelineItem>
              ))}
            </s.Timeline>

            <s.Boolline>
              {[1, 2, 3].map((n) => (
                <s.Col>
                  <h3>12h</h3>
                  <s.BoolItem error={n % 2 === 0} />
                </s.Col>
              ))}
            </s.Boolline>
          </s.TimelineWrapper>
          <s.FooterWrapper>
            {['Ligado', 'Desligado'].map((label, i) => (
              <React.Fragment key={label}>
                <s.Circle color={i === 1 ? colors.green : colors.red} />
                <s.Label>{label}</s.Label>
              </React.Fragment>
            ))}
            <h3>
              última atualização: <span>14:33 - 20/11/20</span>
            </h3>
          </s.FooterWrapper>
        </>
      ) : (
        <s.ChartArea>
          {/* Faixa de eventos (trips/blocks) no periodo selecionado — da
              contexto temporal a curva: se a curva caiu as 14:32 e ha um
              trip marcador as 14:30, a correlacao fica obvia. Passa
              events do estado local pra evitar duplicar fetch (LineChart
              ja faz e o EventTimeline usa). Em modo basico esconde a
              timeline — visual leve, sem distracao. */}
          {isAdvanced && thingId && (
            <EventTimeline
              thingId={thingId}
              period={selectedPeriod}
              eventsExternal={events}
            />
          )}
          {/* Legenda: so quando ha compareSeries (overlay multi-var).
              Mostra cada serie com dot colorido + nome. Operador
              identifica "linha laranja = pressao". */}
          {compareSeries.length > 0 && (
            <s.LegendBar>
              <s.LegendItem>
                <s.LegendSwatch
                  style={{
                    background: statusGradientStops
                      ? colors.lightblue
                      : colors.lightblue,
                  }}
                />
                <s.LegendLabel>{selectedVar.name}</s.LegendLabel>
                {unit && <s.LegendUnit>{unit}</s.LegendUnit>}
              </s.LegendItem>
              {compareSeries.map((series) => {
                // Spinner inline quando data ainda vazio — feedback
                // visual de fetch em progresso. Operador ve "tem
                // alguma coisa carregando" em vez de ficar confuso
                // sem a linha.
                const isLoading = !series.data || series.data.length === 0;
                return (
                  <s.LegendItem key={`legend-${series.ihmname}`}>
                    <s.LegendSwatch style={{ background: series.color }} />
                    <s.LegendLabel>{series.name}</s.LegendLabel>
                    {series.unit && (
                      <s.LegendUnit>{series.unit}</s.LegendUnit>
                    )}
                    {isLoading && (
                      <s.LegendSpinner
                        color={series.color}
                        title="Carregando dados..."
                      />
                    )}
                  </s.LegendItem>
                );
              })}
            </s.LegendBar>
          )}
          <s.ChartCanvas
            ref={chartCanvasRef}
            style={annotationMode ? { cursor: 'crosshair' } : undefined}
          >
          {/* Estados do canvas (prioridade):
              - loadingGraph: chart "desabilitado" (fora) + loader
                CENTRALIZADO. Evita exibir a curva antiga durante o fetch
                (ex.: troca de maquina no carrossel).
              - sem data: EmptyState ("Não há dados").
              - data presente: chart normal. */}
          {loadingGraph ? (
            <s.ChartLoadingState
              style={chartWidth ? { minHeight: chartWidth / 2 } : undefined}
            >
              <s.ChartLoadingSpinnerLarge />
              <span className="message">Carregando dados</span>
            </s.ChartLoadingState>
          ) : data.length === 0 ? (
            <s.EmptyState>
              <span className="icon" aria-hidden="true">
                <svg
                  width="32"
                  height="32"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <line x1="18" y1="20" x2="18" y2="10" />
                  <line x1="12" y1="20" x2="12" y2="4" />
                  <line x1="6" y1="20" x2="6" y2="14" />
                </svg>
              </span>
              <span className="message">
                Não há dados disponíveis neste período
              </span>
            </s.EmptyState>
          ) : (
            /* aspect={2}: largura/altura = 2. Com grid 20 verticais x
               10 horizontais, cada celula do grid fica visualmente
               quadrada (20/10 = 2). Antes era height="100%" que esticava
               horizontalmente, deixando o chart 4:1 ou 5:1 — visual
               muito espremido na vertical.
               Renderiza mesmo com data=[] (durante loading) — Recharts
               desenha os eixos + grid mesmo sem pontos, criando um
               skeleton natural. A linha aparece sozinha quando data
               chega via setData (sem flicker, sem remount). */
            <ResponsiveContainer width="100%" aspect={2}>
              <LineChart
                data={displayData}
                margin={{ top: 12, right: 20, bottom: 4, left: 0 }}
                /* Drag-to-zoom: so em modo avancado. activeTooltipIndex
                   e o indice dentro de displayData no ponto sob o
                   mouse. Capturamos no down, atualizamos no move
                   enquanto pressionado, e committamos no up (se range
                   minimo). */
                onMouseDown={
                  isAdvanced && !annotationMode
                    ? (e) => {
                        if (!e || e.activeTooltipIndex == null) return;
                        // Y vem em PIXEL (chartY) — converte pra data
                        // value usando o dominio atual do Y axis.
                        const yDom =
                          (yAxisConfig && yAxisConfig.domain) ||
                          [yAxisRange.min, yAxisRange.max];
                        const yVal = pixelYToValue(
                          e.chartY,
                          yDom,
                          chartHeight
                        );
                        setSelection({
                          startIdx: e.activeTooltipIndex,
                          endIdx: e.activeTooltipIndex,
                          startY: yVal,
                          endY: yVal,
                        });
                      }
                    : undefined
                }
                onMouseMove={
                  isAdvanced && !annotationMode
                    ? (e) => {
                        if (!selection) return;
                        if (!e || e.activeTooltipIndex == null) return;
                        const yDom =
                          (yAxisConfig && yAxisConfig.domain) ||
                          [yAxisRange.min, yAxisRange.max];
                        const yVal = pixelYToValue(
                          e.chartY,
                          yDom,
                          chartHeight
                        );
                        setSelection({
                          startIdx: selection.startIdx,
                          endIdx: e.activeTooltipIndex,
                          startY: selection.startY,
                          endY: yVal != null ? yVal : selection.endY,
                        });
                      }
                    : undefined
                }
                onMouseUp={
                  isAdvanced
                    ? (e) => {
                        // Modo anotacao: click pontual posiciona a
                        // anotacao no timestamp do data point sob o
                        // cursor. Tem prioridade sobre zoom.
                        if (annotationMode) {
                          if (
                            !e ||
                            e.activeTooltipIndex == null ||
                            !displayData[e.activeTooltipIndex]
                          ) {
                            return;
                          }
                          const ts =
                            displayData[e.activeTooltipIndex].timestamp;
                          if (onAnnotationPlace) onAnnotationPlace(ts);
                          setSelection(null);
                          return;
                        }
                        if (!selection) return;
                        const lo = Math.min(selection.startIdx, selection.endIdx);
                        const hi = Math.max(selection.startIdx, selection.endIdx);
                        if (hi - lo >= MIN_ZOOM_RANGE) {
                          // Commit X via brushRange (efeito local +
                          // emite onZoomChange com Y via useEffect que
                          // observa brushRange e selection.startY/endY).
                          // Cache do Y do selection num ref antes do
                          // setBrushRange pra useEffect ler.
                          pendingZoomYRef.current =
                            Number.isFinite(selection.startY) &&
                            Number.isFinite(selection.endY) &&
                            Math.abs(selection.startY - selection.endY) > 0
                              ? {
                                  yMin: Math.min(selection.startY, selection.endY),
                                  yMax: Math.max(selection.startY, selection.endY),
                                }
                              : null;
                          setBrushRange({ startIndex: lo, endIndex: hi });
                        }
                        setSelection(null);
                      }
                    : undefined
                }
              >
              <YAxis
                yAxisId="primary"
                type="number"
                dataKey="value"
                /* yAxisConfig: memoizado em cima — mesmo objeto alimenta
                   o gradient de status (precisa do domain real, nao do
                   padDomain fallback). Domain e ticks vem alinhados pra
                   Recharts nao filtrar ticks fora do range. */
                /* zoomYDomain (vindo do parent apos box-zoom) tem
                   precedencia — operador zoomou num quadrante e o
                   YAxis precisa refletir essa faixa. Sem zoom Y, usa
                   o yAxisConfig.domain padrao (percentil 2-98). */
                domain={
                  zoomYDomain ||
                  yAxisConfig?.domain ||
                  padDomain(yAxisRange.min, yAxisRange.max)
                }
                /* Quando zoomYDomain ativo, deixa Recharts auto-gerar
                   ticks (nao tem mais 10 niveis "redondos" pre-calc
                   pra essa faixa especifica). yAxisConfig.ticks so
                   serve pro domain original. */
                ticks={zoomYDomain ? undefined : yAxisConfig?.ticks}
                /* allowDataOverflow=true: dominio fica fixo no
                   percentil 2-98 mesmo se houver outliers fora dele.
                   A Line continua renderizando os outliers — vao
                   "estourar" pra cima/baixo do eixo (sinalizando
                   anomalia) sem inflar a escala e esmagar a leitura
                   normal. Sem isso, Recharts auto-expandiria o
                   dominio pra incluir todos os pontos, anulando o
                   percentil. */
                allowDataOverflow
                interval={0}
                tick={{ fill: colors.label, fontSize: 11 }}
                tickLine={false}
                /* So o numero, sem unidade — operador ja tem o contexto
                   pela label do header ("Vazao") + tooltip que mostra
                   unidade ao hover. Antes tinha "760.0 L/s" em cada
                   tick e ficava poluido / cortado em chart estreito. */
                tickFormatter={(v) => Number(v).toFixed(1)}
                width={50}
              />

              {/* YAxes secundarios — um por compareSeries. orientation
                  'right' os empilha do lado direito; width 50px cada
                  pra labels caberem. Cor dos ticks bate com a cor da
                  Line correspondente — leitura visual instantanea
                  ('eixo laranja = pressao'). Sem grid sync — so o
                  YAxis primary alimenta o CartesianGrid. */}
              {compareSeries.map((series) => (
                <YAxis
                  key={`yaxis-${series.ihmname}`}
                  yAxisId={series.ihmname}
                  orientation="right"
                  type="number"
                  dataKey={series.ihmname}
                  domain={compareYDomains[series.ihmname] || ['auto', 'auto']}
                  tick={{ fill: series.color, fontSize: 10 }}
                  tickLine={false}
                  axisLine={false}
                  tickFormatter={(v) =>
                    Number.isFinite(Number(v)) ? Number(v).toFixed(1) : ''
                  }
                  width={50}
                  allowDataOverflow
                />
              ))}

              <XAxis
                ticks={xAxisTicks}
                tickFormatter={(prop) => {
                  // displayData (nao data) — prop e o INDICE no array
                  // renderizado. Com downsampling, mapeia em displayData.
                  if (!displayData[prop]) return '';
                  if (selectedPeriod === 'daily') {
                    // Snap pra hora redonda no display: o data-point mais
                    // proximo de cada hora cheia pode ter timestamp HH:59:30
                    // (sampling do backend nao alinha em :00). Sem o snap,
                    // labels saem '17:59, 18:59...' em vez de '17:00, 18:00'.
                    const ts = displayData[prop].timestamp * 1000;
                    const date = new Date(ts);
                    if (date.getMinutes() >= 30) {
                      date.setHours(date.getHours() + 1);
                    }
                    date.setMinutes(0, 0, 0);
                    return format(date, 'HH:mm');
                  }
                  return formatChartTimestamp(
                    displayData[prop].timestamp,
                    'd/L',
                  );
                }}
                tick={{ fill: colors.label, fontSize: 11 }}
                tickLine={false}
                interval={0}
              />
              {/* Grid: usa explicitamente as ticks dos eixos (passamos os
                  mesmos arrays via ticks prop). syncWithTicks evita que
                  o Recharts caia no modo "1 linha por categoria" — que
                  acontece quando XAxis e category-mode e tinha 1 linha
                  por data point (visual sobrecarregado). */}
              <CartesianGrid
                stroke={colors.disabled}
                strokeDasharray="3 3"
                opacity={0.5}
                syncWithTicks
              />
              {referenceLines.map((ref) => (
                <ReferenceLine
                  yAxisId="primary"
                  key={`${ref.label}-${ref.value}`}
                  y={ref.value}
                  stroke={ref.color}
                  strokeDasharray="4 4"
                  strokeOpacity={0.6}
                  // 'hidden' (nao 'extendDomain'): o limite de range so
                  // aparece se cair DENTRO do dominio Y ajustado aos dados.
                  // Com 'extendDomain' o eixo se esticava pra incluir um
                  // threshold distante (ex.: +9 numa maquina que gera -100
                  // kW), grudando a linha na borda "fora de contexto" e
                  // espremendo a curva real. Agora o eixo segue os dados e
                  // thresholds irrelevantes pra janela simplesmente somem.
                  ifOverflow="hidden"
                />
              ))}
              {/* Linha de media — referencia horizontal tracejada cinza
                  no valor medio do periodo. Da contexto numerico direto
                  no chart: 'o normal e aqui'. */}
              {dataStats && (
                <ReferenceLine
                  yAxisId="primary"
                  y={dataStats.avg}
                  stroke={colors.label}
                  strokeDasharray="3 6"
                  strokeOpacity={0.4}
                  label={{
                    value: `méd ${unit ? `(${unit})` : ''}`,
                    position: 'insideTopLeft',
                    fill: colors.label,
                    fontSize: 9,
                    fontWeight: 600,
                    offset: 4,
                  }}
                />
              )}
              {/* Trend line — regressao linear sobre toda a serie.
                  So em modo avancado (analise, nao glance). Segment com
                  yAxisId='primary' liga o ponto inicial (x=0) ao final
                  (x = last index). Tracejado mais largo (6 3) pra
                  diferenciar da linha de media (3 6). */}
              {trendSegment && (
                <ReferenceLine
                  yAxisId="primary"
                  stroke={colors.lightblue}
                  strokeDasharray="6 3"
                  strokeOpacity={0.55}
                  strokeWidth={1.5}
                  segment={trendSegment}
                  ifOverflow="extendDomain"
                  label={{
                    value: 'tendência',
                    position: 'insideBottomRight',
                    fill: colors.lightblue,
                    fontSize: 9,
                    fontWeight: 600,
                    offset: 6,
                  }}
                />
              )}
              {/* Retangulo de selecao durante drag — feedback visual
                  em tempo real. Box 2D (x1/x2 + y1/y2) — selecao
                  livre em qualquer quadrante do chart. Translucido pra
                  nao esconder a curva embaixo. */}
              {selection &&
                selection.startIdx !== selection.endIdx &&
                Number.isFinite(selection.startY) &&
                Number.isFinite(selection.endY) && (
                  <ReferenceArea
                    yAxisId="primary"
                    x1={Math.min(selection.startIdx, selection.endIdx)}
                    x2={Math.max(selection.startIdx, selection.endIdx)}
                    y1={Math.min(selection.startY, selection.endY)}
                    y2={Math.max(selection.startY, selection.endY)}
                    stroke={colors.lightblue}
                    strokeOpacity={0.4}
                    fill={colors.lightblue}
                    fillOpacity={0.15}
                  />
                )}
              {/* Bandas de PARADA (estado operacional). Translucido
                  cinza escuro — comunica visualmente "maquina parada
                  aqui" sem competir com a curva. Renderizado antes
                  das outras refs pra ficar atras. Running fica
                  transparente (default). */}
              {operationalStoppedBands.map((b) => (
                <ReferenceArea
                  yAxisId="primary"
                  key={`opstop-${b.key}`}
                  x1={b.startIdx}
                  x2={b.endIdx}
                  stroke="none"
                  fill={
                    colors.title === 'light'
                      ? 'rgba(0, 0, 0, 0.06)'
                      : 'rgba(255, 255, 255, 0.06)'
                  }
                  fillOpacity={1}
                  ifOverflow="extendDomain"
                />
              ))}
              {/* Anotacoes do operador (persistencia localStorage no
                  modal). Linha continua amarela com bandeirinha do
                  texto + botao × pra remover inline (quando o parent
                  passa onRemoveAnnotation). */}
              {annotationRefs.map((a) => (
                <ReferenceLine
                  yAxisId="primary"
                  key={`annot-${a.id}`}
                  x={a.x}
                  stroke={colors.yellow || '#fbbf24'}
                  strokeWidth={1.5}
                  strokeOpacity={0.75}
                  label={(props) => (
                    <AnnotationLabel
                      {...props}
                      text={a.text}
                      color={colors.yellow || '#fbbf24'}
                      onRemove={
                        onRemoveAnnotation
                          ? () => onRemoveAnnotation(a.id)
                          : null
                      }
                    />
                  )}
                />
              ))}
              {/* Change-points: mudancas de regime detectadas (delta
                  significativo no mean shift). Tracejado roxo discreto
                  com label do delta — operador ve quando a variavel
                  'mudou de comportamento'. */}
              {changePointRefs.map((cp) => (
                <ReferenceLine
                  yAxisId="primary"
                  key={`cp-${cp.x}`}
                  x={cp.x}
                  stroke={colors.lightblue}
                  strokeDasharray="2 4"
                  strokeOpacity={0.5}
                  strokeWidth={1}
                  label={{
                    value: `Δ ${cp.delta >= 0 ? '+' : ''}${cp.delta.toFixed(1)}`,
                    position: 'insideTopRight',
                    fill: colors.lightblue,
                    fontSize: 9,
                    fontWeight: 700,
                  }}
                />
              ))}
              {/* Anotacoes verticais de eventos (trips=amarelo, blocks=vermelho) */}
              {eventReferenceLines.map((ref) => (
                <ReferenceLine
                  yAxisId="primary"
                  key={`evt-ref-${ref.x}-${ref.label}-${ref.reason}`}
                  x={ref.x}
                  stroke={ref.color}
                  strokeWidth={1.5}
                  strokeOpacity={0.7}
                  label={{
                    value: ref.label,
                    position: 'top',
                    fill: ref.color,
                    fontSize: 13,
                  }}
                />
              ))}
              {/* #5 Marcador do evento selecionado no Log — linha solida
                  azul, mais grossa que as demais, com label 'evento' pra
                  correlacionar o instante do alarme/trip com a curva. */}
              {focusReferenceX !== null && (
                <ReferenceLine
                  yAxisId="primary"
                  x={focusReferenceX}
                  stroke={colors.lightblue}
                  strokeWidth={2}
                  strokeOpacity={0.95}
                  ifOverflow="extendDomain"
                  label={{
                    value: 'evento',
                    position: 'insideTopLeft',
                    fill: colors.lightblue,
                    fontSize: 10,
                    fontWeight: 700,
                  }}
                />
              )}
              {/* Gradiente pra area fill abaixo da curva atual — da volume
                  visual sem competir com a linha em si. */}
              <defs>
                <linearGradient
                  id="lineAreaGradient"
                  x1="0"
                  y1="0"
                  x2="0"
                  y2="1"
                >
                  <stop
                    offset="0%"
                    stopColor={colors.lightblue}
                    stopOpacity={0.32}
                  />
                  <stop
                    offset="100%"
                    stopColor={colors.lightblue}
                    stopOpacity={0.02}
                  />
                </linearGradient>
                {/* Gradient de status: linha muda de cor conforme zona
                    (verde Normal → laranja Warning → vermelho Erro).
                    Stops vem do statusGradientStops memoizado — null
                    quando a variavel nao tem ranges definidos (ex.:
                    vazao), entao caimos no stroke azul default. */}
                {statusGradientStops && (
                  <linearGradient
                    id="statusGradient"
                    x1="0"
                    y1="0"
                    x2="0"
                    y2="1"
                  >
                    {statusGradientStops.map((stop, idx) => (
                      <stop
                        // eslint-disable-next-line react/no-array-index-key
                        key={`${stop.offset}-${stop.color}-${idx}`}
                        offset={`${stop.offset}%`}
                        stopColor={stop.color}
                      />
                    ))}
                  </linearGradient>
                )}
              </defs>
              {/* Area: mesma key 'value' que a linha, mas com fill
                  gradiente. Stroke none — a Line abaixo desenha o
                  contorno colorido. Recharts plota ambas no mesmo grid.
                  Em multi-variavel a area visual fica confusa (3 series
                  competindo) — escondemos quando ha compareSeries. */}
              {compareSeries.length === 0 && (
                <Area
                  yAxisId="primary"
                  type="linear"
                  dataKey="value"
                  stroke="none"
                  fill="url(#lineAreaGradient)"
                  isAnimationActive={false}
                  activeDot={false}
                />
              )}
              {/* Ghost line — periodo anterior, tracejada cinza, abaixo da
                  curva atual visualmente (renderiza ANTES pra ficar atras). */}
              {showGhost && ghostData.length > 0 && (
                <Line
                  yAxisId="primary"
                  type="linear"
                  dataKey="ghostValue"
                  name="anterior"
                  stroke={colors.label}
                  strokeWidth={1.5}
                  strokeDasharray="5 5"
                  strokeOpacity={0.55}
                  dot={false}
                  isAnimationActive={false}
                  connectNulls
                />
              )}
              {/* Linhas das compareSeries — renderizadas ANTES da
                  primaria pra primaria ficar visualmente "em cima"
                  (importante quando ha overlap). Cada Line usa o
                  yAxisId proprio da serie. */}
              {compareSeries.map((series) => (
                <Line
                  key={`line-${series.ihmname}`}
                  yAxisId={series.ihmname}
                  type="linear"
                  dataKey={series.ihmname}
                  name={series.name}
                  stroke={series.color}
                  strokeWidth={1.8}
                  strokeOpacity={0.9}
                  dot={false}
                  activeDot={{
                    r: 3,
                    fill: series.color,
                    stroke: colors.card,
                    strokeWidth: 1,
                  }}
                  isAnimationActive={false}
                  connectNulls
                />
              ))}
              <Line
                yAxisId="primary"
                type="linear"
                dataKey="value"
                name="atual"
                /* Stroke dinamico: quando a variavel tem ranges
                   definidos (warning/erro), usa gradient vertical que
                   muda de cor conforme valor entra em cada zona —
                   leitura visual instantanea. Sem ranges (vazao etc.),
                   fallback pra azul default. */
                stroke={
                  statusGradientStops
                    ? 'url(#statusGradient)'
                    : colors.lightblue
                }
                strokeWidth={2}
                dot={dotRenderer}
                /* Active dot: aparece no ponto sob o cursor durante hover —
                   da feedback visual claro de qual valor o tooltip esta
                   mostrando. */
                activeDot={{
                  r: 4,
                  fill: colors.lightblue,
                  stroke: colors.card,
                  strokeWidth: 2,
                }}
                isAnimationActive={false}
              />

              <Tooltip
                contentStyle={{
                  background: colors.card,
                  border: `1px solid ${colors.disabled}`,
                  borderRadius: 8,
                  fontSize: 12,
                }}
                /* Crosshair vertical sutil — cor label opacity 0.35 em
                   vez do branco stark default (que cortava visualmente
                   no hover). */
                cursor={{
                  stroke: colors.label,
                  strokeWidth: 1,
                  strokeOpacity: 0.4,
                  strokeDasharray: '3 3',
                }}
                labelStyle={{ color: colors.label }}
                itemStyle={{ color: colors.darkerblue }}
                /* labelFormatter mostra timestamp UMA VEZ no topo do
                   tooltip — antes era null e cada item replicava
                   "as XX:YY", redundante em multi-variavel. Prefixo
                   "Hora:" pra ficar claro que e a hora do data-point
                   (operador as vezes nao identificava o HH:mm solto
                   acima do valor). Em periodos longos (semana/mes) usa
                   formato completo 'd/L/y - HH:mm' pra contextualizar
                   o dia tambem; em 'daily' so HH:mm e suficiente. */
                labelFormatter={(label, payload) => {
                  // Fonte 1: timestamp no payload do ponto sob o cursor.
                  let ts =
                    payload &&
                    payload.length &&
                    payload[0].payload &&
                    payload[0].payload.timestamp;
                  // Fonte 2 (fallback): o XAxis nao tem dataKey, entao
                  // `label` e o INDICE no displayData. Quando o payload
                  // nao carrega timestamp (acontecia no modo basico — o
                  // tooltip saia so com o valor, sem hora), resolvemos
                  // o timestamp pelo indice direto no displayData.
                  if (
                    !ts &&
                    typeof label === 'number' &&
                    displayData[label]
                  ) {
                    ts = displayData[label].timestamp;
                  }
                  if (!ts) return null;
                  const formatted =
                    selectedPeriod === 'daily'
                      ? formatChartTimestamp(ts, 'HH:mm')
                      : formatChartTimestamp(ts);
                  return `Hora: ${formatted}`;
                }}
                /* Mostra TODAS as series com nome + unidade
                   correspondente. Compara o name da Line com o nome
                   da compareSeries pra extrair a unidade certa
                   (primaria usa unit do header; compareSeries usa
                   series.unit propria). */
                formatter={(value, name) => {
                  // Arredonda pra 2 casas decimais — evita artefatos de
                  // float ('27.179999999999996' em vez de '27.18'). 2
                  // casas e suficiente pra qualquer variavel de sensor;
                  // se precisar precisao maior, operador exporta CSV.
                  const v =
                    typeof value === 'number' && Number.isFinite(value)
                      ? Number(value.toFixed(2))
                      : value;
                  if (name === 'anterior') {
                    return [`${v} ${unit}`, 'Anterior'];
                  }
                  if (name === 'atual') {
                    return [`${v} ${unit}`, selectedVar.name || 'Atual'];
                  }
                  // Match com compareSeries por name
                  const cs = compareSeries.find((s) => s.name === name);
                  if (cs) {
                    return [`${v} ${cs.unit}`, cs.name];
                  }
                  return [v, name];
                }}
              />
              {/* Brush: range selector embaixo do chart. Operador
                  arrasta as alcas pra dar zoom numa janela de tempo
                  especifica (ex.: investigar o dip das 07:50 sem
                  trocar pro periodo 'semana'). So em modo avancado —
                  no basico o operador usa os botoes Mes/Semana/Dia.
                  PERF: o filho LineChart nao define `data` prop —
                  Recharts cascateia o `data={displayData}` do parent.
                  Como displayData ja vem LTTB-reduzido, o mini-preview
                  da Brush renderiza so ~1000 pontos em vez dos
                  multiSeriesData crus. Sem essa cascata, o brush
                  duplicava o custo de render do chart. */}
              {isAdvanced && (
                <Brush
                  dataKey="timestamp"
                  height={40}
                  travellerWidth={10}
                  stroke={colors.lightblue}
                  fill={
                    colors.title === 'light'
                      ? 'rgba(0, 0, 0, 0.025)'
                      : 'rgba(255, 255, 255, 0.04)'
                  }
                  /* Controlled — startIndex/endIndex vem do brushRange.
                     Permite que drag-to-zoom no chart atualize a posicao
                     do Brush automaticamente. onChange sincroniza no
                     sentido inverso: arrastar o Brush atualiza
                     brushRange (que tambem alimenta o reset button no
                     proximo commit). */
                  startIndex={brushRange ? brushRange.startIndex : undefined}
                  endIndex={brushRange ? brushRange.endIndex : undefined}
                  onChange={(range) => {
                    // range = { startIndex, endIndex } — Recharts dispara
                    // a cada mexida da alca. Atualizamos brushRange so
                    // se mudou (evita loop com o controlled startIndex).
                    if (!range) return;
                    if (
                      !brushRange ||
                      range.startIndex !== brushRange.startIndex ||
                      range.endIndex !== brushRange.endIndex
                    ) {
                      setBrushRange({
                        startIndex: range.startIndex,
                        endIndex: range.endIndex,
                      });
                    }
                  }}
                >
                  <LineChart>
                    <Line
                      type="linear"
                      dataKey="value"
                      stroke={colors.lightblue}
                      strokeWidth={1}
                      dot={false}
                      isAnimationActive={false}
                    />
                  </LineChart>
                </Brush>
              )}
            </LineChart>
            </ResponsiveContainer>
          )}
          {/* Loader agora e centralizado (ChartLoadingState acima),
              substituindo o chart durante o fetch — sem badge no canto. */}
          </s.ChartCanvas>
        </s.ChartArea>
      )}

      {/* Footer: Stats (Min/Med/Max) + ultima atualizacao depois do
          grafico, alinhados com o eixo X. No modo avancado, esse footer
          e ESCONDIDO e o modal (AdvancedModal) renderiza seu proprio
          footer no rodape do dialog inteiro (UX-decision: fica abaixo
          do chart+stats panel pra nao quebrar a barra de stats lateral). */}
      {chartType !== 'bool' && !isAdvanced && (dataStats || lastUpdate) && (
        <s.ChartFooter>
          {dataStats ? (
            <s.StatsInline>
              <s.StatItem>
                <s.StatLabel>Mín</s.StatLabel>
                <s.StatValue>
                  {formatStat(dataStats.min)}
                  {unit && <small>{unit}</small>}
                </s.StatValue>
              </s.StatItem>
              <s.StatItem>
                <s.StatLabel>Méd</s.StatLabel>
                <s.StatValue>
                  {formatStat(dataStats.avg)}
                  {unit && <small>{unit}</small>}
                </s.StatValue>
              </s.StatItem>
              <s.StatItem>
                <s.StatLabel>Máx</s.StatLabel>
                <s.StatValue>
                  {formatStat(dataStats.max)}
                  {unit && <small>{unit}</small>}
                </s.StatValue>
              </s.StatItem>
            </s.StatsInline>
          ) : (
            <span />
          )}
          {lastUpdate && (
            <s.LastUpdate
              title={
                isLive
                  ? `Recebendo dados em tempo real — última: ${lastUpdateFull}`
                  : `Sem atualizações recentes — última: ${lastUpdateFull}`
              }
            >
              {isLive && <s.LiveDot />}
              <strong>{lastUpdate}</strong>
            </s.LastUpdate>
          )}
        </s.ChartFooter>
      )}
    </s.Container>
  );
};

// React.memo: o LineChart e re-renderizado pelo parent (aplicacao,
// card-supervisorio, etc.) cada vez que outros estados nao
// relacionados mudam — subscription IOT atualiza a cada 2-3s, hover
// no velocimetro re-renderiza o parent, etc. Com props memoizadas
// pelo parent (selectedVar, things, compareSeries...), o shallow
// compare default do React.memo evita re-renders desnecessarios do
// LineChart inteiro (que tem useMemos pesados em cascata).
export default React.memo(LineChartComponent);
